import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { API_URL } from "../apiConfig";


function AdminLoginForm() {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({ email: "", password: "", general: "" });
    const [otpStep, setOtpStep] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);
    const [loading, setLoading] = useState(false);



    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState("");
    const [resendError, setResendError] = useState("");
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendLocked, setResendLocked] = useState(false);

    useEffect(() => {
        if (!resendCooldown) return;
        const interval = setInterval(() => {
            setResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [resendCooldown]);

    const handleResendOtp = async () => {
        setResendError('');
        setResendMessage('');
        setResendLoading(true);
        try {
            const response = await fetch(`${API_URL}/resend-admin-login-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const data = await response.json();
            if (!response.ok) {
                setResendError(data.message || 'Unable to resend OTP.');
                if (data.cooldownSeconds) {
                    setResendCooldown(data.cooldownSeconds);
                    if (data.cooldownSeconds > 300) setResendLocked(true);
                }
                return;
            }
            setResendMessage('A new code has been sent to your email.');
            if (data.cooldownSeconds) setResendCooldown(data.cooldownSeconds);
            setResendLocked(false);
        } catch (err) {
            setResendError('Server error. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    const { setCurrentUser, setToken } = useAuth();
    const isValidEmail = (value) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);

    const logoImg = require('../assets/logonobg.png');

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setErrors({ email: "", password: "", general: "" });

        const nextErrors = { email: "", password: "", general: "" };
        if (!email.trim()) nextErrors.email = "Email is required.";
        else if (!isValidEmail(email)) nextErrors.email = "Enter a valid email address.";
        if (!password) nextErrors.password = "Password is required.";
        if (nextErrors.email || nextErrors.password) {
            setErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password, portal: 'admin' }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.verified === false) {
                    setErrors({ 
                        email: "", 
                        password: "", 
                        general: (
                            <span>
                                Your account is not verified. <br/>
                                <button 
                                    onClick={() => navigate(`/verify?email=${email}&redirect=admin`)}
                                    style={{ 
                                        background: 'none', 
                                        border: 'none', 
                                        color: '#DEC731', 
                                        textDecoration: 'underline', 
                                        cursor: 'pointer',
                                        padding: '0',
                                        font: 'inherit'
                                    }}
                                >
                                    Click here to verify now
                                </button>
                            </span>
                        ) 
                    });
                    return;
                }
                setErrors({ email: "", password: "", general: data.message || "Login failed" });
                return;
            }

            if (data.status === 'otp_required') {
                setOtpStep(true);
                setErrors({ email: "", password: "", general: "" });
                return;
            }

            const user = data.user || {};
            const role = (user.role || "").toLowerCase();

            if (role !== "admin" && role !== "staff") {
                setErrors({ email: "", password: "", general: "Access denied. Production and Delivery staff must use the Mobile App to log in." });
                setLoading(false);
                return;
            }

            localStorage.setItem("user", JSON.stringify({ ...user, role: role || user.role }));
            localStorage.setItem("token", data.token);
            localStorage.setItem('adminLastActivity', Date.now().toString());
            setCurrentUser({ ...user, role: role || user.role });
            if (setToken && data.token) setToken(data.token);

            let roleName = "Staff";
            if (role === 'admin') roleName = "Admin";

            
            
            navigate("/admin");
        } catch (error) {
            console.error("Login error:", error);
            setErrors({ email: "", password: "", general: "Server error. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setErrors({ email: "", password: "", general: "" });
        
        if (!otp.trim()) {
            setErrors({ general: "Please enter the OTP sent to your email." });
            return;
        }

        setOtpLoading(true);
        try {
            const response = await fetch(`${API_URL}/admin-login-verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp }),
            });

            const data = await response.json();

            if (!response.ok) {
                setErrors({ general: data.message || "OTP verification failed" });
                return;
            }

            const user = data.user || {};
            const role = (user.role || "").toLowerCase();

            if (role !== "admin" && role !== "staff") {
                setErrors({ general: "Access denied." });
                return;
            }

            localStorage.setItem("user", JSON.stringify({ ...user, role: role || user.role }));
            localStorage.setItem("token", data.token);
            if (data.refreshToken) {
                localStorage.setItem("refreshToken", data.refreshToken);
            }
            localStorage.setItem('adminLastActivity', Date.now().toString());
            setCurrentUser({ ...user, role: role || user.role });
            if (setToken && data.token) setToken(data.token);

            navigate("/admin");
        } catch (error) {
            console.error("OTP verification error:", error);
            setErrors({ general: "Server error. Please try again." });
        } finally {
            setOtpLoading(false);
        }
    };

    const css = `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Montserrat:wght@300;400;500;600&display=swap');
 
        * { box-sizing: border-box; margin: 0; padding: 0; }
 
        .admin-page {
            min-height: 100vh;
            width: 100%;
            background-color: #2E4B4C;
            display: flex;
            font-family: 'Montserrat', sans-serif;
            overflow: hidden;
        }
 
        .admin-left {
            width: 45%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 24px;
            border-right: 1px solid rgba(222,199,49,0.4);
            padding: 60px 40px;
            position: relative;
        }
        .logo-wrap {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
        }
        .logo-img {
            width: 130px;
            height: 130px;
            object-fit: contain;
            filter: drop-shadow(0 4px 24px rgba(222,199,49,0.18));
        }
        .brand-name {
            font-family: 'Cormorant Garamond', serif;
            font-size: 38px;
            font-weight: 500;
            color: #EDE8D8;
            letter-spacing: 3px;
            text-align: center;
            line-height: 1.15;
            text-transform: uppercase;
        }
        .brand-sub {
            font-size: 11px;
            font-weight: 400;
            color: rgba(237,232,216,0.55);
            letter-spacing: 5px;
            text-transform: uppercase;
            text-align: center;
            margin-top: -10px;
        }
 
        /* ── RIGHT PANEL ── */
        .admin-right {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 80px;
        }
        .login-box {
            width: 100%;
            max-width: 380px;
        }
        .login-welcome {
            font-family: 'Cormorant Garamond', serif;
            font-size: 42px;
            font-weight: 400;
            color: #EDE8D8;
            margin-bottom: 6px;
            letter-spacing: 1px;
            text-align: center;
        }
        .login-tagline {
            font-size: 11px;
            font-weight: 400;
            color: rgba(237,232,216,0.5);
            letter-spacing: 3px;
            text-transform: uppercase;
            margin-bottom: 44px;
             text-align: center;
        }
 
        /* inputs */
        /* inputs */
        .admin-input {
            width: 100%;
            padding: 14px 16px;
            background: #F4F8F7;
            border: 1px solid transparent;
            border-radius: 4px;
            color: #333333;
            font-family: 'Montserrat', sans-serif;
            font-size: 14px;
            font-weight: 500;
            outline: none;
            margin-bottom: 16px;
            transition: border-color 0.2s, box-shadow 0.2s;
        }
        .admin-input::placeholder { color: #888888; }
        .admin-input:focus {
            border-color: #DEC731;
            box-shadow: 0 0 0 2px rgba(222,199,49,0.2);
        }
 
        .password-container {
            position: relative;
            width: 100%;
            margin-bottom: 16px;
        }
        .password-container .admin-input {
            margin-bottom: 0;
            padding-right: 40px;
        }
        .password-toggle {
            position: absolute;
            right: 14px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            color: #6B7280;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            border-radius: 40px;
            transition: color 0.2s, background-color 0.2s;
        }
        .password-toggle:hover {
            color: #2E4B4C;
            background-color: rgba(46,75,76,0.08);
        }

 
        /* button */
        .admin-btn {
            width: 100%;
            padding: 14px;
            background: #DEC731;
            color: #2E4B4C;
            border: none;
            border-radius: 4px;
            font-family: 'Montserrat', sans-serif;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 2px;
            text-transform: uppercase;
            cursor: pointer;
            margin-top: 6px;
            transition: background 0.2s, transform 0.15s;
        }
        .admin-btn:hover:not(:disabled) {
            background: #e8d440;
            transform: translateY(-1px);
        }
        .admin-btn:disabled { opacity: 0.6; cursor: not-allowed; }
 
        /* errors */
        .err-text {
            color: #f87171;
            font-size: 11px;
            margin-top: -8px;
            margin-bottom: 10px;
            letter-spacing: 0.3px;
        }
        .err-general {
            color: #f87171;
            font-size: 12px;
            text-align: center;
            margin: 8px 0;
        }
 
        .back-link {
            display: block;
            text-align: center;
            margin-top: 24px;
            color: rgba(237,232,216,0.6);
            font-size: 13px;
            text-decoration: none;
            transition: color 0.2s;
            letter-spacing: 0.5px;
        }
        .back-link:hover { color: #DEC731; }

        @media (max-width: 768px) {
            .admin-left { display: none; }
            .admin-right { padding: 40px 20px; }
            .login-welcome { font-size: 32px; }
            .login-tagline { margin-bottom: 30px; }
        }
    `;

    return (
        <>
            <style>{css}</style>
            <div className="admin-page">

                {/* ── LEFT: Logo + Brand ── */}
                <div className="admin-left">
                    <div className="logo-wrap">
                        <img src={logoImg} alt="Ukit Logo" className="logo-img" />
                        <div>
                            <div className="brand-name">Úkit<br />Kapampangan</div>
                            <div className="brand-sub">Est. Furniture Crafts</div>
                        </div>
                    </div>
                </div>

                {/* ── RIGHT: Login Form ── */}
                <div className="admin-right">
                    <div className="login-box">
                        <div className="login-welcome">Welcome</div>
                        <div className="login-tagline">Sign in to Admin Dashboard</div>

                        {otpStep ? (
                            <form onSubmit={handleVerifyOtp} noValidate>
                                <div style={{ color: '#EDE8D8', fontSize: '13px', textAlign: 'center', marginBottom: '20px' }}>
                                    We've sent a 6-digit code to your email.
                                </div>
                                <input
                                    type="text"
                                    name="otp"
                                    autoComplete="off"
                                    placeholder="Enter OTP"
                                    className="admin-input"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    maxLength="6"
                                    style={{ textAlign: 'center', letterSpacing: '4px', fontSize: '16px' }}
                                />
                                {errors.general && (
                                    <div className="err-general">{errors.general}</div>
                                )}
                                <button
                                    type="submit"
                                    className="admin-btn"
                                    disabled={otpLoading}
                                >
                                    {otpLoading ? "Verifying..." : "Verify Code"}
                                </button>
                                
                                {/* Resend Section */}
                                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                                    {resendMessage && (
                                    <div style={{ color: '#059669', fontSize: '13px', marginBottom: '12px', fontWeight: '500' }}>
                                        {resendMessage}
                                    </div>
                                    )}
                                    {resendError && (
                                    <div style={{ color: '#e74c3c', fontSize: '13px', marginBottom: '12px', fontWeight: '500' }}>
                                        {resendError}
                                    </div>
                                    )}
                                    <button
                                    type="button"
                                    onClick={handleResendOtp}
                                    disabled={resendLoading || resendCooldown > 0 || resendLocked}
                                    style={{
                                        backgroundColor: 'transparent',
                                        color: (resendCooldown > 0 || resendLocked) ? '#9ca3af' : '#DEC731',
                                        border: `1px solid ${(resendCooldown > 0 || resendLocked) ? '#6B7280' : '#DEC731'}`,
                                        padding: '10px 20px',
                                        borderRadius: '10px',
                                        cursor: (resendLoading || resendCooldown > 0 || resendLocked) ? 'not-allowed' : 'pointer',
                                        opacity: (resendLoading || resendCooldown > 0 || resendLocked) ? 0.65 : 1,
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s',
                                        fontFamily: "'Montserrat', sans-serif",
                                        width: '100%',
                                        marginTop: '8px'
                                    }}
                                    >
                                    {resendLoading
                                        ? 'Sending...'
                                        : resendCooldown > 0
                                        ? `Resend Code (${resendCooldown}s)`
                                        : 'Resend Code'}
                                    </button>
                                </div>

                                <button 
                                    type="button"
                                    onClick={() => setOtpStep(false)}
                                    style={{
                                        width: '100%',
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'rgba(237,232,216,0.6)',
                                        marginTop: '16px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    Back to Login
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleLogin} noValidate>
                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="off"
                                    placeholder="Admin Email"
                                    className="admin-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                {errors.email && <div className="err-text">{errors.email}</div>}

                                <div className="password-container">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        autoComplete="new-password"
                                        placeholder="Password"
                                        className="admin-input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>

                                {errors.password && <div className="err-text">{errors.password}</div>}

                                {errors.general && (
                                    <div className="err-general">{errors.general}</div>
                                )}

                                <div style={{ textAlign: 'right', marginTop: '-8px', marginBottom: '16px' }}>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/admin/forgot-password')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'rgba(237,232,216,0.7)',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            letterSpacing: '0.5px'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = '#DEC731'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(237,232,216,0.7)'}
                                    >
                                        Forgot Password?
                                    </button>
                                </div>

                                <button
                                    type="submit"
                                    className="admin-btn"
                                    disabled={loading}
                                >
                                    {loading ? "Logging In..." : "Login"}
                                </button>
                            </form>
                        )}

                        <button 
                            onClick={() => navigate('/login')} 
                            className="back-btn"
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'transparent',
                                color: 'rgba(237,232,216,0.7)',
                                border: '1px solid rgba(237,232,216,0.3)',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '600',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                marginTop: '24px',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#DEC731';
                                e.currentTarget.style.borderColor = '#DEC731';
                                e.currentTarget.style.backgroundColor = 'rgba(222,199,49,0.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'rgba(237,232,216,0.7)';
                                e.currentTarget.style.borderColor = 'rgba(237,232,216,0.3)';
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            &lt;- Back to Customer Login
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AdminLoginForm;